import { Typography } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { GridAlignment } from "@mui/x-data-grid-pro";
import { cloneDeep } from "lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import HeaderSelectionPopover from "../../../components/DataGrid/headerSelectionPopover/HeaderSelectionPopover";
import { DandDitem } from "../../../components/DragAndDropList/DragAndDropList";
import { ActionLink } from "../../../components/Link/ActionLink/ActionLink";
import { AppContext } from "../../../core/context/appContextProvider";
import RoutingPaths from "../../../core/routing/routingPaths";
import { getColumnOrder, saveColumnOrder } from "../../../services/columnOrder.service";
import { useClientEffect } from "../../../services/hooks/useClientsEffect/useClientEffect.hooks";
import {
  getPortfolioCompanies,
  getSoiTransactionSummary,
} from "../../../services/portfolioCompanies.service";
import { CurrencyFormat, DateTimeFormat, NumberFormat } from "../../../utils/helpers/format.helper";
import { useEffectAsync } from "../../../utils/hooks/useEffectAsync.hook";
import { ColumnOrder, ViewItemsEntity } from "../../../utils/types/columnOrder";
import { DataGridColDef } from "../../../utils/types/listItems";
import { PortfolioCompany } from "../../../utils/types/portfolioCompany.type";
import { InvestmentTransactionDefaults } from "../InvestmentTransactions/InvestmentTransactionList.hooks";
import { CategorySelectionPopover } from "./categorySelectionPopover/CategorySelectionPopover";
import {
  getDefaultCategoryFieldOrder,
  getDefaultDataFieldOrder,
} from "./ScheduleOfInvestments.defaults";
import {
  CategoryFieldDef,
  LevelColors,
  SoiBaseData,
  SoiGridRow,
  ValueFieldDef,
} from "./ScheduleOfInvestments.types";

export const useScheduleOfInvestments = () => {
  const defaultView = 'Unrealized_SOI';
  const { fundId } = useParams<{ fundId: string }>();
  const history = useHistory();

  const [dateFilter, setDateFilter] = useState<Date | null | undefined>();
  const [currSOIview, setCurrSOIview] = useState(defaultView);
  const [portfolioCompanies, setPortfolioCompanies] = useState<PortfolioCompany[]>([]);
  const [transSummaryItems, setTransSummaryItems] = useState<any>();
  const [soiBaseData, setSoiBaseData] = useState<SoiBaseData[]>();
  const [rebuildSoiGridData, setRebuildSoiGridData] = useState(0);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [categoryFieldOrder, setCategoryFieldOrder] = useState<CategoryFieldDef[]>([]);
  const [dataFieldOrder, setDataFieldOrder] = useState<ValueFieldDef[]>([]);
  const [lockedPrompt, setLockedPrompt] = useState<boolean>(false);
  const { state } = useContext(AppContext);

  const clientId = state.loginUser.clientId;
  const { client } = useClientEffect(clientId !== "new" ? clientId : undefined);

  useEffectAsync(
    async (isCanceled) => {
      let tempPortfolioCompanies: PortfolioCompany[];

      if (portfolioCompanies.length === 0) {
        const portfolioCompaniesResponse = await getPortfolioCompanies();

        if (isCanceled()) return;

        tempPortfolioCompanies = portfolioCompaniesResponse.items;
      } else {
        tempPortfolioCompanies = portfolioCompanies;
      }

      let dateThruToUse: Date | null;

      if (!dateFilter) {
        dateThruToUse = new Date();
      } else if (isNaN(dateFilter?.getTime())) {
        dateThruToUse = null;
      } else {
        dateThruToUse = dateFilter;
      }

      let transSummaryItemsResponse = [];

      if (dateThruToUse) {
        const transSummaryResponse = await getSoiTransactionSummary( dateThruToUse);

        if (isCanceled()) return;

        if (fundId) {
          transSummaryItemsResponse = transSummaryResponse.items.filter(
            (item: any) => item.investmentTransactionDto.fund.id === fundId
          );
        } else {
          transSummaryItemsResponse = transSummaryResponse.items;
        }
      }

      buildSoiBaseData(tempPortfolioCompanies, transSummaryItemsResponse, currSOIview);

      const categoryFieldOrderResponse = await getCategoryFieldOrder(defaultView);
      const dataFieldOrderResponse = await getDataFieldOrder(defaultView);

      setCategoryFieldOrder(categoryFieldOrderResponse);
      setDataFieldOrder(dataFieldOrderResponse);

      setPortfolioCompanies(tempPortfolioCompanies);
      setTransSummaryItems(transSummaryItemsResponse);
      setIsLoadingList(false);
      setRebuildSoiGridData(prevVal => prevVal + 1);

      // For some reason MUI grid wont hide columns on initial 
      // display so need to force refresh which makes it work
      setTimeout(() => {
        setRebuildSoiGridData(prevVal => prevVal + 1);
      }, 1);

    }, [dateFilter]
  );


  function ensureCorrectColSortOrder(cols: CategoryFieldDef[]) {
    const orderedCols = cols.sort((a, b) => a.order - b.order);

    for(let i=0; i < orderedCols.length; i++){
      const col = orderedCols[i];

      col.order = i+1;
    }
  }

  async function getCategoryFieldOrder(soiViewName: string) {
    const colOrderResponse = await getColumnOrder(`SOI_CATEGORY_${soiViewName}_VIEW`, clientId);

    if(!colOrderResponse || !colOrderResponse.viewItems || colOrderResponse.viewItems!.length === 0) {
      const defaultCatOrder = getDefaultCategoryFieldOrder(!fundId);

      return defaultCatOrder;
    }

    const savedCatOrder = colOrderResponse.viewItems!.map(col => {
      return {
        headerName: col.label,
        name: col.code,
        order: col.order,
        visible: col.visible
      } as CategoryFieldDef;
    });

    ensureCorrectColSortOrder(savedCatOrder);

    return savedCatOrder!;
  }

  function saveCategoryFieldOrder(updatedCategoryFieldOrder: CategoryFieldDef[]) {
    if(!updatedCategoryFieldOrder || updatedCategoryFieldOrder.length === 0) return;

    const viewItems = updatedCategoryFieldOrder.map(catCol => {
      return {
        label: catCol.headerName,
        code: catCol.name,
        order: catCol.order,
        visible: catCol.visible
      } as ViewItemsEntity;
    });

    const newColumnOrder: ColumnOrder = {
      clientId: clientId,
      viewKey: `SOI_CATEGORY_${currSOIview}_VIEW`,
      viewItems: viewItems
    };

    saveColumnOrder(newColumnOrder);
  }

  function handleDandDVisibleChange(itemId: string) {
    const catField = categoryFieldOrder.find(
      (field) => field.name === itemId
    )!;

    catField.visible = !catField.visible;
    setCategoryFieldOrder(cloneDeep(categoryFieldOrder));
    setRebuildSoiGridData(rebuildSoiGridData + 1);

    saveCategoryFieldOrder(categoryFieldOrder);
  }

  function handleDandDItemsChange(items: DandDitem[]) {
    const newCategoryFieldOrder = items.map((item, index) => {
      const fieldDef = item.data as CategoryFieldDef;

      fieldDef.order = index + 1;
      return fieldDef;
    });

    setCategoryFieldOrder(cloneDeep(newCategoryFieldOrder));
    setRebuildSoiGridData(rebuildSoiGridData + 1);

    saveCategoryFieldOrder(newCategoryFieldOrder);
  }


  async function getDataFieldOrder(soiViewName: string) {
    const defaultDataFieldOrder = getDefaultDataFieldOrder(soiViewName);
    const colOrderResponse = await getColumnOrder(`SOI_FIELD_${soiViewName}_VIEW`, clientId);

    if(!colOrderResponse || !colOrderResponse.viewItems || colOrderResponse.viewItems!.length === 0) { 
      return defaultDataFieldOrder;
    }

    const savedDataFieldOrder: ValueFieldDef[] = [];

    for(let i=0; i<colOrderResponse.viewItems.length; i++) {
      const col = colOrderResponse.viewItems[i];
      const dataField =  defaultDataFieldOrder.find(f => f.name === col.code);

      if(!dataField) continue;
      
      savedDataFieldOrder.push( {
          headerName: col.label,
          name: col.code,
          order: col.order,
          visible: col.visible,
          numberFormat: dataField.numberFormat
        } as ValueFieldDef
      );
      
    }

    const defaultsNotInSaved = defaultDataFieldOrder.filter( d => !savedDataFieldOrder.find(s => s.name === d.name));

    defaultsNotInSaved.forEach(d => { savedDataFieldOrder.push(d); });

    ensureCorrectColSortOrder(savedDataFieldOrder);
    
    return savedDataFieldOrder;
  }

  function saveDataFieldOrder(updatedDataFieldOrder: ValueFieldDef[]) {
    if(!updatedDataFieldOrder || updatedDataFieldOrder.length === 0) return;

    const viewItems = updatedDataFieldOrder.map(catCol => {
      return {
        label: catCol.headerName,
        code: catCol.name,
        order: catCol.order,
        visible: catCol.visible
      } as ViewItemsEntity;
    });

    const newColumnOrder: ColumnOrder = {
      clientId: clientId,
      viewKey: `SOI_FIELD_${currSOIview}_VIEW`,
      viewItems: viewItems
    };

    saveColumnOrder(newColumnOrder);
  }


  function handleFieldFilterChange(fieldName: string) {
    const dataField = dataFieldOrder.find(
      (field) => field.name === fieldName
    )!;

    dataField.visible = !dataField.visible;
    setDataFieldOrder(cloneDeep(dataFieldOrder));
    setRebuildSoiGridData(rebuildSoiGridData + 1);

    saveDataFieldOrder(dataFieldOrder);
  }

  function handleColumnOrderChange(oldIndex: number, newIndex: number) {
    const sourceCol = dataFieldOrder.find((field) => field.order === oldIndex)!;
    const targetCol = dataFieldOrder.find((field) => field.order === newIndex)!;

    sourceCol.order = newIndex;
    targetCol.order = oldIndex;

    setDataFieldOrder(cloneDeep(dataFieldOrder));
    setRebuildSoiGridData(rebuildSoiGridData + 1);

    saveDataFieldOrder(dataFieldOrder);
  } 




  async function handleSoiViewChange(newView: string) {
    const categoryFieldOrderResponse = await getCategoryFieldOrder(newView);
    const newDataFieldOrder = await getDataFieldOrder(newView);

    buildSoiBaseData(portfolioCompanies, transSummaryItems, newView);

    setCurrSOIview(newView);
    setCategoryFieldOrder(categoryFieldOrderResponse);
    setDataFieldOrder(newDataFieldOrder);
    setRebuildSoiGridData(rebuildSoiGridData + 1);
  }

  function buildSoiBaseData(
    portfolioCompanies: PortfolioCompany[],
    transSummary: any[],
    soiView: string
  ) {
    const tmpCatRows: SoiBaseData[] = [];

    transSummary.forEach((ts) => {
      const portfolioCompany = portfolioCompanies.find(
        (pc) => ts.investmentTransactionDto.portfolioCompany.id === pc.id
      );

      if (!portfolioCompany) return;
      if (!ts.investmentTransactionDto) return;

      
      const itd = ts.investmentTransactionDto;
      let isd = ts.investmentSaleDto;

      if(!isd || isd.saleCount === 0) isd = undefined;

      const ivd = ts.investmentValuationDto;
      const itssd = ts.investmentStockSplitList
        .sort((a:any,b:any) => DateTimeFormat.compareDateOnly(b.date, a.date))
        .find((t: any) => t !== undefined); // get first if it exists

      const itcd = ts.investmentTransactionConversionList
        .sort((a:any,b:any) => DateTimeFormat.compareDateOnly(b.date, a.date))
        .find((t: any) => t !== undefined); // get first if it exists

      if(soiView === 'Realized_SOI' && !isd) return; 

      itd.investmentTranche = itd.investmentTranche !== "null" ? itd.investmentTranche : "(Tranch not set)";
      itd.securityType = itd.securityType !== "null" ? itd.securityType : "(Security not set)";

      const writeOff = isd ? isd.totalAmount === 0  : false;

      let itdQuantity: number;  // shares
      
      if(isd) {
        if(soiView === 'Realized_SOI') {
          itdQuantity = isd.quantity;
        } else {
          if(itcd) {
            itdQuantity = (itcd.quantity ?? 0) - isd.quantity;
          } else {
            itdQuantity = itd.quantity - (isd.quantity ?? 0);
          }
        }
      } else if(itcd) {
        itdQuantity = itcd.quantity ?? 0;
      } else if(itssd) {
        itdQuantity = itssd.quantity ?? 0;
      } else {
        itdQuantity = itd.quantity ?? 0;
      }

      let itdTotalCost: number; // cost
      
      if(soiView === 'Realized_SOI') {
        if(writeOff) {
          itdTotalCost = itd.totalCost;
        } else if(itcd) {
          itdTotalCost = itdQuantity ? isd.quantity * itcd.costPerQuantity : isd.totalAmount;
        } else {
          itdTotalCost = itdQuantity ? isd.quantity * itd.costPerQuantity : itd.totalCost;
        }
      } else {
        if(isd && itdQuantity === 0) return;

        if(itcd) {
          itdTotalCost = itdQuantity ? itcd.costPerQuantity * itdQuantity : itcd.totalCost;
        } else {
          itdTotalCost = itdQuantity ? itd.costPerQuantity * itdQuantity : itd.totalCost;
        }
      }


      const ivdTotalAmount = ivd ? (ivd?.totalAmount ?? 0) : itdTotalCost; // unrealizedValue

      const isdTotalAmount = isd?.totalAmount ?? 0; // realizedValue
      const realizedGainLoss = isdTotalAmount === 0 && !writeOff ? 0 : isdTotalAmount - itdTotalCost;
      const unrealizedGainLoss = ivdTotalAmount - itdTotalCost;
      const totalGainLoss = realizedGainLoss + unrealizedGainLoss;

      const catRow: SoiBaseData = {
        portfolioCompanyId: portfolioCompany.id,
        currencyCode: itd.currency,
        categoryProps: {
          companyName: portfolioCompany.name,
          industrySector: portfolioCompany.industrySector,
          country: portfolioCompany.address.country,
          holdingStatus: portfolioCompany.holdingStatus,
          isPublic: portfolioCompany.isPublic ? "Public" : "Private",

          fund: itd.fund.name,
          tranche: itcd ? itcd.investmentTranche : itd.investmentTranche,
          security: itcd ? itcd.securityType : itd.securityType,
        },
        dataProps: {
          shares: itdQuantity,
          cost: itdTotalCost,
          unrealizedValue: ivdTotalAmount,
          unrealizedGainLoss: unrealizedGainLoss,
          realizedValue: isdTotalAmount,
          realizedGainLoss: realizedGainLoss,
          totalGainLoss: totalGainLoss,
          costPerQuantity: 0 // calced later in summary
        },
      };

      tmpCatRows.push(catRow);
    });

    setSoiBaseData(tmpCatRows);
  }

  const soiGridData = useMemo<{
    soiGridRows: SoiGridRow[];
    dataGridColDefs: DataGridColDef[];
  }>(() => getSoiGridData(), [rebuildSoiGridData]);

  function getSoiGridData() {
    if (!soiBaseData) return { soiGridRows: [], dataGridColDefs: [] };

    const gridRows: SoiGridRow[] = [];
    let rowId = 0;

    const footerRow: SoiGridRow = {
      id: (rowId++).toString(),
      portfolioCompanyId: "",
      categorySource: "",
      categoryName: "Investment Totals",
      parentCategoryPath: "",
      level: 1,
      currencyCode: soiBaseData.find(x => x.currencyCode)?.currencyCode as string,
    };

    soiBaseData?.forEach((baseDataRow) => {
      let currDataCategoryPath = "";

      // for each soiBaseData, go though each user specified category and find its place
      categoryFieldOrder
        .filter((cfo) => cfo.visible)
        .sort((a, b) => a.order - b.order)
        .forEach((cfo, index) => {
          // @ts-ignore: expression error
          const currDataRowCat = baseDataRow.categoryProps[cfo.name];
          const currLevel = index + 1;
          const parentDataCatPath = currDataCategoryPath;

          currDataCategoryPath = `${currDataCategoryPath}/${currDataRowCat}`;
          let gridRowToUpdate: SoiGridRow | undefined = undefined;
          let rowInsertPos = -1;

          rowInsertPos = gridRows.findIndex(
            (r) =>
              parentDataCatPath === r.parentCategoryPath &&
              currDataRowCat <= r.categoryName
          );
          if (rowInsertPos === -1) {
            // if we make it here then we need to find position of end of parent.
            // This will also handle parents that are currently empty

            const parentCatPos = gridRows.findIndex(
              (r) =>
                parentDataCatPath ===
                `${r.parentCategoryPath}/${r.categoryName}`
            );

            for (let r = parentCatPos + 1; ; r++) {
              const tmpRow = gridRows[r];

              if (!tmpRow || tmpRow.level < currLevel) {
                rowInsertPos = r;
                break;
              }
            }
          } else {
            // if we make it here then we know rowInsertPos is either exaction position or place we need to insert
            const tmpRow = gridRows[rowInsertPos];

            if (tmpRow.categoryName === currDataRowCat) {
              gridRowToUpdate = tmpRow; // we found exact position so we'll just update gridRowToUpdate data values
            }
          }

          if (!gridRowToUpdate) {
            // create and insert new GridRow

            const newGridRow: SoiGridRow = {
              id: (rowId++).toString(),
              portfolioCompanyId: baseDataRow.portfolioCompanyId,
              categorySource: cfo.name,
              categoryName: currDataRowCat,
              parentCategoryPath: parentDataCatPath,
              level: currLevel,
              currencyCode: baseDataRow.currencyCode,
            };

            gridRows.splice(rowInsertPos, 0, newGridRow);

            gridRowToUpdate = newGridRow;
          }


          const sortedDataFieldOrder = dataFieldOrder.sort((a,b) => a.order-b.order);

          sortedDataFieldOrder.forEach(dataField => {
            let valueToAdd = 0;

            try {
              // @ts-ignore: expression error
              valueToAdd = baseDataRow.dataProps[dataField.name];
              if (isNaN(valueToAdd)) {
                valueToAdd = 0;
              }
            } catch {
              valueToAdd = 0;
            }

            // @ts-ignore: expression error
            gridRowToUpdate[dataField.name] = (gridRowToUpdate[dataField.name] ?? 0) + valueToAdd;


            if(currLevel === 1) {
              // @ts-ignore: expression error
              footerRow[dataField.name] = (footerRow[dataField.name] ?? 0) + valueToAdd;
            }

          });
        });
    });



    gridRows.push(footerRow);

    const visibleLength = categoryFieldOrder.filter(c => c.visible).length;

    // summary calculations should be done here
    gridRows.forEach(r => {
      if( r.level === visibleLength && r.shares) {
        r.costPerQuantity = r.cost / r.shares;
      } else {
        r.costPerQuantity = undefined;
      }
    });


    const tempColHeaders = buildColumnHeaders();

    return { soiGridRows: gridRows, dataGridColDefs: tempColHeaders };
  }

  function buildColumnHeaders() {
    const tempColHeaders: DataGridColDef[] = [];

    dataFieldOrder.forEach((dataField) => {
      tempColHeaders.push({
        index: dataField.order,
        field: dataField.name,
        headerName: dataField.headerName,
        headerAlign: "center" as GridAlignment,
        editable: false,
        disableReorder: false,
        minWidth: 50,
        sortable: false,
        resizable: true,
        align: "right" as GridAlignment,
        flex: 150,
        hide: !dataField.visible,
        renderCell: (params: GridRenderCellParams) => {
          const dataField = dataFieldOrder.find((f) => f.name === params.colDef.field)!;

          const value = params.value;
          const numberFormat = value ? dataField.numberFormat : ''; // if there is no value then display empty string
          let valueFormated: string;

          switch (numberFormat) {
            case "Currency.0":
              valueFormated = CurrencyFormat(params.row.currencyCode, 0).format(value);
              break;
            case "Currency.2":
              valueFormated = CurrencyFormat(params.row.currencyCode, 2).format(value);
              break;
            case "Integer-use_commas":
              valueFormated = NumberFormat(0, true).format(value);
              break;
            case "Decimal-use-comas.2":
              if(String(value).includes('.')) {
                valueFormated = NumberFormat(2, true).format(value);
              } else {
                valueFormated = NumberFormat(0, true).format(value);
              }
              break;
            default:
              valueFormated = value;
          }

          return (
            <Typography
              align={"right"}
              fontWeight={params.row.level === 1 ? "bold" : "normal"}
              fontSize={params.row.level === 1 ? "1.15rem" : "normal"}
            >
              {valueFormated}
            </Typography>
          );
        },
      });
    });

    insertBaseDefCols(tempColHeaders);

    return tempColHeaders;
  }

  function insertBaseDefCols(catColDefs: DataGridColDef[]) {
    const dAndDitems = categoryFieldOrder.map((colField) => {
      return {
        id: colField.name,
        name: colField.headerName,
        data: colField,
      } as DandDitem;
    });

    const categoryColDef: DataGridColDef = {
      index: 1,
      field: "_categoryName",
      editable: false,
      disableReorder: true,
      headerName: "Categories",
      width: 400,
      minWidth: 300,
      sortable: false,
      resizable: true,
      renderHeader: (_params) => {
        return (
          <CategorySelectionPopover
            dAndDitems={dAndDitems}
            onChange={handleDandDItemsChange}
            onVisibleChange={handleDandDVisibleChange}
          />
        );
      },
      renderCell: renderCategoryCell,
    };

    function renderCategoryCell(params: GridRenderCellParams) {
      const row: SoiGridRow = params.row;
      const levelColor = LevelColors[row.level - 1];

      const typographyElement = 
        <Typography
          align="left"
          fontWeight={row.level === 1 ? "bold" : "normal"}
          color={levelColor}
          marginLeft={`${row.level * 25}px`}
        >
          {row.categoryName}
        </Typography>;

      return (
        <>  {
          row.categorySource === 'companyName' ?  (
              <ActionLink
              id={`link_investment_transaction_${row.id}`}
              onClick={() => {
                history.push(
                  `${RoutingPaths.InvestmentTransactions}`, 
                  { filters: { portfolioCompanies: [row.categoryName] } } as InvestmentTransactionDefaults
                );
              }}
            >            
              { typographyElement }
            </ActionLink>
          ) : (
            typographyElement
          )
        } </> 
      );
      
    }

     

    catColDefs.splice(0, 0, categoryColDef);

    const fieldFilterColDef: DataGridColDef = {
      index: catColDefs.length + 1,
      field: "_fieldFilterColDef",
      editable: false,
      disableReorder: true,
      headerName: "",
      width: 50,
      minWidth: 50,
      sortable: false,
      resizable: false,
      renderHeader: (_params) => {
        return (
          <HeaderSelectionPopover
            id="data_grid_column_selector"
            headerFields={catColDefs.filter(
              (c) =>
                c.field !== "_categoryName" && c.field !== "_fieldFilterColDef"
            )}
            handleUpdateHeader={handleFieldFilterChange}
          />
        );
      },
      renderCell: (_params: GridRenderCellParams) => {
        return <></>;
      },
    };

    catColDefs.push(fieldFilterColDef);
  }

 
  const showLockedDialog = () => {
    setLockedPrompt(true);
  };

  return {
    isLoadingList,
    currSOIview, handleSoiViewChange,
    soiGridData,
    handleColumnOrderChange,
    dateFilter,
    setDateFilter,
    lockedPrompt,
    setLockedPrompt,
    showLockedDialog,
    client,
  };
};
